import React from 'react';
import { useHourlyActivityQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { HourlyActivity } from '@/api/models';

/**
 * Returns an `HourlyActivity` snapshot for the widget.
 *
 * Wraps `useHourlyActivityQuery` in the widget's consumer contract so the
 * View + container don't have to learn the query's internals. Mapping
 * rules mirror the other Sales hooks.
 */
export const useHourlyActivity = () => {
    const query = useHourlyActivityQuery();
    const { refetch } = query;

    const data = React.useMemo<HourlyActivity | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
